import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { CustomerApiService } from './customer-api.service';
import { CustomerSessionService } from './customer-session.service';
import { CustomerMeResponse } from './customer-dtos';
import { displayName, initialsOf, maskedContact } from
  '../../features/table-connect/account/account-format.util';

/**
 * TC-A1 — root-level cache of the signed-in customer's profile so the global
 * header/account-chip can render initials + name without every screen (and the
 * header) re-calling getMe() on each navigation. Loads at most once per
 * authenticated token; clears on logout/401; refreshable after a profile edit.
 *
 * It never wires auth itself — the customerTokenInterceptor attaches the
 * X-Customer-Token to getMe() automatically.
 */
@Injectable({ providedIn: 'root' })
export class CustomerProfileStore {
  private api = inject(CustomerApiService);
  private session = inject(CustomerSessionService);

  private readonly _me = signal<CustomerMeResponse | null>(null);
  private readonly _loading = signal(false);
  /** Load-once guard — the token we last fetched for. */
  private fetchedForToken: string | null = null;

  readonly me = this._me.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly initials = computed(() => {
    const m = this._me();
    return m ? initialsOf(m.firstName, m.lastName) : '·';
  });
  readonly name = computed(() => {
    const m = this._me();
    return m ? displayName(m) : 'there';
  });
  readonly contact = computed(() => {
    const m = this._me();
    return m ? maskedContact(m) : '';
  });

  constructor() {
    // When the session drops (explicit logout OR a cross-tab logout that resets
    // the session signals), clear the cache so the chip can't show stale initials.
    effect(() => {
      if (!this.session.isAuthenticated()) this.reset();
    });
  }

  /**
   * Called by the header on each navigation with the active token. No-ops unless
   * authenticated and not already fetched for this token (so it hits the network
   * at most once per signed-in token).
   */
  ensureLoaded(publicToken: string | null): void {
    if (!publicToken || !this.session.isAuthenticated()) return;
    if (this.fetchedForToken === publicToken && this._me() !== null) return;
    this.load(publicToken);
  }

  /** Force a re-fetch (e.g. after the customer edits their profile). */
  refresh(publicToken: string): void {
    this.fetchedForToken = null;
    this.load(publicToken);
  }

  /** Drop the cache (logout / token switch). */
  reset(): void {
    this._me.set(null);
    this._loading.set(false);
    this.fetchedForToken = null;
  }

  private load(publicToken: string): void {
    this._loading.set(true);
    this.api.getMe().subscribe(res => {
      this._loading.set(false);
      if (res.ok) {
        this._me.set(res.data);
        this.fetchedForToken = publicToken;
        return;
      }
      // Session invalid — clear it so the chip flips to "Sign in".
      if (res.status === 401) {
        this.reset();
        this.session.clear(publicToken);
      }
      // Other errors: leave the cache as-is; the chip falls back to '·'.
    });
  }
}
